/* LexiLauncher — floating button + chat window mounted globally */
import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import LexiChat from './LexiChat';
import { LEXI_ENABLED } from '../../lib/features';

const LexiLauncher = () => {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  // Listen for global "open lexi" events from anywhere in the app
  useEffect(() => {
    const handler = (e) => {
      setPrefill(e.detail?.prefill || '');
      setOpen(true);
      setHasUnread(false);
    };
    window.addEventListener('lexi:open', handler);
    window.addEventListener('lxt:lexi:open', handler); // newer namespaced alias used by HomepageLexi
    return () => {
      window.removeEventListener('lexi:open', handler);
      window.removeEventListener('lxt:lexi:open', handler);
    };
  }, []);

  // Show hint once per session
  useEffect(() => {
    const seen = sessionStorage.getItem('lexi_hint_seen');
    if (!seen) {
      const t = setTimeout(() => setShowHint(true), 3500);
      const t2 = setTimeout(() => { setShowHint(false); sessionStorage.setItem('lexi_hint_seen', '1'); }, 9500);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, []);

  if (!LEXI_ENABLED) return null;

  return (
    <>
      <LexiChat
        open={open}
        onClose={() => setOpen(false)}
        onMinimize={() => setOpen(false)}
        prefill={prefill}
      />

      {/* Floating button */}
      {!open && (
        <div className="fixed bottom-20 right-4 z-[999] flex items-center gap-2" data-testid="lexi-launcher">
          {showHint && (
            <div className="bg-white border border-gray-200 rounded-full shadow-lg px-3 py-1.5 text-xs font-medium text-gray-800 animate-pulse hidden sm:flex items-center gap-1">
              Ask Lexi <span aria-hidden>→</span>
            </div>
          )}
          <button
            onClick={() => { setPrefill(''); setOpen(true); }}
            aria-label="Open Lexi — your career mentor"
            className="relative w-14 h-14 rounded-full bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700 text-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all flex items-center justify-center group"
            data-testid="lexi-floating-btn"
          >
            <Zap size={22} fill="currentColor" className="group-hover:rotate-12 transition-transform" />
            {hasUnread && (
              <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
            )}
            <span className="sr-only">Ask Lexi</span>
          </button>
        </div>
      )}
    </>
  );
};

export default LexiLauncher;
